import { Capacitor } from '@capacitor/core'
import {
  API_V2_PATH,
  KANGURO_INTEGRATED_API_PROXY,
} from '@/shared/constants/api'
import { AUTH_HEADER_SCHEME } from '@/shared/constants/auth'
import {
  ApiProblemError,
  toApiProblemError,
} from '@/shared/helpers/api-problem'
import { PET_CLAIM_LIST_KIND } from '@/shared/types/global.types'
import type {
  ClaimDocument,
  PetClaim,
  PetClaimList,
  PetClaimListKind,
} from '@/shared/types/global.types'

const isNative = Capacitor.isNativePlatform()
const APP_BASE_URL = process.env.BUN_PUBLIC_APP_BASE_URL ?? ''

// Claims go through the integrated API proxy so the browser and Capacitor app
// don't depend on the API's CORS. The API only returns the caller's own claims
function toUrl(path: string) {
  const proxyPath = `${KANGURO_INTEGRATED_API_PROXY}${path}`
  return isNative ? `${APP_BASE_URL}${proxyPath}` : proxyPath
}

export type CreatePetClaimBody = {
  policyId: number
  /** US cents */
  amount: number
  invoiceDate: string
  description?: string
  invoices: File[]
  pledgeOfHonor: File
  petPicture?: File
}

function toFormData(body: CreatePetClaimBody): FormData {
  const form = new FormData()
  form.append('policy_id', String(body.policyId))
  form.append('amount', String(body.amount))
  form.append('invoice_date', body.invoiceDate)
  if (body.description) form.append('description', body.description)
  body.invoices.forEach((file) => form.append('invoices', file, file.name))
  form.append('pledge_of_honor', body.pledgeOfHonor, body.pledgeOfHonor.name)
  if (body.petPicture) {
    form.append('pet_picture', body.petPicture, body.petPicture.name)
  }
  return form
}

// Some environments (staging) answer with a 2xx and no body while the claim is
// still being processed in the background
async function readOptionalJson<T>(response: Response): Promise<T | undefined> {
  const text = await response.text()
  return text ? (JSON.parse(text) as T) : undefined
}

/** Resolves `undefined` when the API accepted the claim without returning it */
export async function createPetClaim(
  token: string,
  language: string,
  body: CreatePetClaimBody,
): Promise<PetClaim | undefined> {
  // CapacitorHttp can't send FormData, so this always goes through fetch.
  // No Content-Type: the browser sets the multipart boundary itself.
  const response = await fetch(toUrl(API_V2_PATH.PET_CLAIMS), {
    method: 'POST',
    headers: {
      Authorization: `${AUTH_HEADER_SCHEME} ${token}`,
      'Accept-Language': language,
    },
    body: toFormData(body),
  })
  if (!response.ok) throw await toApiProblemError(response)
  return readOptionalJson<PetClaim>(response)
}

export type UpdatePetClaimBody = {
  /** US cents; omitted to keep, '' to clear */
  amount?: number | ''
  invoiceDate?: string
  /** '' clears it */
  description?: string
  /**
   * Only send these when the invoice set changes: the API makes
   * `keepInvoiceIds` + `newInvoices` the claim's complete invoice set
   */
  keepInvoiceIds?: string[]
  newInvoices?: File[]
  pledgeOfHonor?: File
  petPicture?: File
}

function toUpdateFormData(body: UpdatePetClaimBody): FormData {
  const form = new FormData()
  if (body.amount !== undefined) form.append('amount', String(body.amount))
  if (body.invoiceDate !== undefined) {
    form.append('invoice_date', body.invoiceDate)
  }
  if (body.description !== undefined) {
    form.append('description', body.description)
  }
  body.keepInvoiceIds?.forEach((id) => form.append('keep_invoice_ids', id))
  body.newInvoices?.forEach((file) => form.append('invoices', file, file.name))
  if (body.pledgeOfHonor) {
    form.append('pledge_of_honor', body.pledgeOfHonor, body.pledgeOfHonor.name)
  }
  if (body.petPicture) {
    form.append('pet_picture', body.petPicture, body.petPicture.name)
  }
  return form
}

export async function updatePetClaim(
  token: string,
  language: string,
  claimId: string,
  body: UpdatePetClaimBody,
): Promise<PetClaim> {
  const response = await fetch(
    toUrl(`${API_V2_PATH.PET_CLAIMS}/${encodeURIComponent(claimId)}`),
    {
      method: 'PATCH',
      headers: {
        Authorization: `${AUTH_HEADER_SCHEME} ${token}`,
        'Accept-Language': language,
      },
      body: toUpdateFormData(body),
    },
  )
  if (!response.ok) throw await toApiProblemError(response)
  return (await response.json()) as PetClaim
}

const POLICY_ID_PARAM = 'policy_id'

// The Claims API takes the policies to read as a comma-separated list
function toPolicyIdParam(policyIds: string[]): string {
  return policyIds.join(',')
}

/**
 * Files a `fileable` claim with the insurance core. No body: everything was
 * supplied while building it. Safe to retry; the API never files twice
 */
export async function submitPetClaim(
  token: string,
  language: string,
  claimId: string,
): Promise<PetClaim> {
  const response = await fetch(
    toUrl(`${API_V2_PATH.CLAIMS}/${encodeURIComponent(claimId)}`),
    {
      method: 'POST',
      headers: {
        Authorization: `${AUTH_HEADER_SCHEME} ${token}`,
        'Accept-Language': language,
      },
    },
  )
  if (!response.ok) throw await toApiProblemError(response)
  return (await response.json()) as PetClaim
}

async function claimsRequest<T>(
  token: string,
  language: string,
  path: string,
): Promise<T> {
  const response = await fetch(toUrl(path), {
    headers: {
      Authorization: `${AUTH_HEADER_SCHEME} ${token}`,
      'Accept-Language': language,
    },
  })
  if (!response.ok) throw await toApiProblemError(response)
  return (await response.json()) as T
}

/** Drafts are claims not yet submitted; filed ones include old app claims */
export function getPetClaimList(
  token: string,
  language: string,
  kind: PetClaimListKind,
  policyIds: string[],
  page: number,
  perPage: number,
): Promise<PetClaimList> {
  const params = new URLSearchParams({
    [POLICY_ID_PARAM]: toPolicyIdParam(policyIds),
    page: String(page),
    per_page: String(perPage),
  })
  return claimsRequest<PetClaimList>(
    token,
    language,
    `${API_V2_PATH.CLAIMS}/${kind}?${params}`,
  )
}

// Claims API maximum page size
const LOOKUP_PER_PAGE = 50

/**
 * The Claims API has no GET by id (only PATCH), so a claim is looked up in the
 * caller's drafts, then filed lists, page by page
 */
export async function getPetClaim(
  token: string,
  language: string,
  claimId: string,
  policyIds: string[],
): Promise<PetClaim> {
  for (const kind of Object.values(PET_CLAIM_LIST_KIND)) {
    let page = 1
    let totalPages = 1
    while (page <= totalPages) {
      const list = await getPetClaimList(
        token,
        language,
        kind,
        policyIds,
        page,
        LOOKUP_PER_PAGE,
      )
      const claim = list.data.find((item) => item.id === claimId)
      if (claim) return claim
      totalPages = list.pagination?.total_pages ?? 0
      page += 1
    }
  }
  throw new ApiProblemError(404, {
    title: 'Claim Not Found',
    status: 404,
    detail: 'No claim with that id was found.',
  })
}

export async function getPetClaimDocuments(
  token: string,
  language: string,
  claimId: string,
): Promise<ClaimDocument[]> {
  const response = await claimsRequest<{ data: ClaimDocument[] }>(
    token,
    language,
    `${API_V2_PATH.CLAIMS}/${encodeURIComponent(claimId)}${API_V2_PATH.CLAIM_DOCUMENTS}`,
  )
  return response.data
}

export async function downloadPetClaimDocument(
  token: string,
  claimId: string,
  documentId: string,
): Promise<Blob> {
  const response = await fetch(
    toUrl(
      `${API_V2_PATH.CLAIMS}/${encodeURIComponent(claimId)}${API_V2_PATH.CLAIM_DOCUMENTS}/${encodeURIComponent(documentId)}`,
    ),
    { headers: { Authorization: `${AUTH_HEADER_SCHEME} ${token}` } },
  )
  if (!response.ok) throw await toApiProblemError(response)
  return response.blob()
}
